import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { LazyQueryExecFunction } from '@apollo/client';
import styles from './UnitEditForm.module.scss';
import { TextInput, TextInputValue } from '@components/text-input/TextInput';
import Select from 'src/components/select/Select';
import SelectMulti from 'src/components/select-multi/SelectMulti';
import RadioGroup from "src/components/radio-group/RadioGroup";
import Checkbox from 'src/components/checkbox/Checkbox';
import ImageUploadForm from '../edit/edit-content/image-form/ImageUploadForm';
import VideoForm from '@components/video-form/VideoForm'
import TagsForm from '../edit/edit-content/tags-form/TagsForm';
import AnswersForm from '../edit/edit-content/feedback-form-editor/form-component/form-answers/AnswersForm';
import { GetUrlQuery, Exact, useGetVideoUrlLazyQuery, Video, VideoUploadInput } from '@api/graphql-codegen';
import PreviewForm from '@src/components/preview-form/PreviewForm';
import { handleUploadVideo } from '@services/Video';
import { EditUnit, SelectOption } from '@types';
import { MultiLineTextInput } from '@src/components/text-input/MultiLineTextInput';
import { ageOptions, categoryComfortLevelOptions, categoryExerciseStyleOptions, categoryMoodOptions1, categorySkillsOptions, categoryStructureOptions, gradeLabelPositionOptions, standaloneLessonsSelCompetencies } from '@constants';
import { addDays, formatDate } from '@src/utils/time';
import { kebabCase } from 'change-case';

export const validationErrors = {
  name: '',
  uniqueName: '',
  subHeading: '',
  skill: '',
  pace: '',
  courses: '',
  teacherPreps: [],
  parentPreps: [],
  answers1: [],
  answers2: [],
  description: '',
  studyGuides: []
};

export type ValidationErrorKeys = keyof typeof validationErrors;

type Props = {
  unit: EditUnit;
  onChange: (p: Partial<EditUnit>) => void;
  setLoading: (state: boolean) => void;
  skillOptions: SelectOption[];
  coursesOptions: SelectOption[];
  getUrl: LazyQueryExecFunction<GetUrlQuery, Exact<{input: VideoUploadInput}>>
  fileUploadingPercent: number;
  setFileUploadingPercent: (percent: number) => void;
  setErrors: (e: Partial<Record<ValidationErrorKeys, any>>) => void;
  errors: Partial<Record<ValidationErrorKeys, any>>;
  isHomeEdUnit?: boolean;
};

export const options = [
  {label: "Australia", value: "Australia"},
  {label: "Canada", value: "Canada"},
  {label: "USA", value: "USA"},
  {label: "Canada & USA", value: "Canada & USA"},
  {label: "All Countries", value: "All Countries"}
]

export const unitOptions = [
  {label: "Primary", value: "Primary"},
  {label: "Additional", value: "Additional"}
]

export enum TabKeys {
  School = "School",
  HomeEd = "Home Ed"
}

export const labelOptions = [
  {label: "Empty", value: "Empty"},
  {label: "New", value: "New"},
  {label: "Coming Soon", value: "Coming Soon"},
  {label: "Updated", value: "Updated"}
]

export const coverOptions = [
  {label: "Old Cover", value: "old"},
  {label: "New Cover", value: "new"}
]

function UnitEditTabContent(props: Props) {
  const { onChange, unit, setLoading, skillOptions, coursesOptions, getUrl, fileUploadingPercent, setFileUploadingPercent, errors, setErrors, isHomeEdUnit } = props;
  const [dataFile, setDataFile] = useState<File | undefined>();

  const courses = useMemo(() => JSON.parse(unit?.courseId || '[]'), [unit?.courseId]);
  const tags = useMemo(() => JSON.parse(unit?.tags || '[]'), [unit?.tags]);
  const topics = useMemo(() => JSON.parse(unit?.topics || '[]'), [unit?.topics]);
  const teacherPreps = useMemo(() => JSON.parse(unit?.teacherBox || '[]'), [unit?.teacherBox]);
  const parentPreps = useMemo(() => JSON.parse(unit?.parentBox || '[]'), [unit?.parentBox]);
  const answers1 = useMemo(() => JSON.parse(unit?.box1 || '[]'), [unit?.box1]);
  const answers2 = useMemo(() => JSON.parse(unit?.box2 || '[]'), [unit?.box2]);
  const competency = useMemo(() => JSON.parse(unit?.competency || '[]'), [unit?.competency])
  const studyGuides = useMemo(() => JSON.parse(unit?.unitStudyBox || '[]'), [unit?.unitStudyBox]);
  const categories = useMemo(() => JSON.parse(unit?.categories || '[]'), [unit?.categories])

  const [label, setLabel] = useState('');
  const [comingSoonAt, setComingSoonAt] = useState<string>('');

  const [cover, setCover] = useState(coverOptions[0].value);
  const [structure, setStructure] = useState('')
  const [skill, setSkill] = useState('')
  const [comfortLevel, setComfortLevel] = useState('')
  const [mood, setMood] = useState('')
  const [exerciseStyle, setExerciseStyle] = useState('')

  useEffect(() => {
    if (unit.isNew) {
      setLabel(labelOptions[1].value)
    } else if (unit.isComingSoon) {
      setLabel(labelOptions[2].value)
    } else if (unit.isUpdated) {
      setLabel(labelOptions[3].value)
    } else {
      setLabel(labelOptions[0].value)
    }
  }, [unit])

  useEffect(() => {
    if (unit.isNewCover) {
      setCover(coverOptions[1].value)
    } else {
      setCover(coverOptions[0].value)
    }
  }, [unit.isNewCover])

  useEffect(() => {
    if (categories['Structure']) {
      setStructure(categories['Structure'])
    }
    if (categories['Skill']) {
      setSkill(categories['Skill'])
    }
    if (categories['Comfort Level']) {
      setComfortLevel(categories['Comfort Level'])
    }
    if (categories['Mood']) {
      setMood(categories['Mood'])
    }
    if (categories['Exercise Style']) {
      setExerciseStyle(categories['Exercise Style'])
    }
  }, [categories])

  useEffect(() => {
    if (unit?.comingSoonAt) {
      let d;
      if (unit.comingSoonAt.includes('-')) {
        d = new Date(unit.comingSoonAt);
      } else {
        d = new Date(parseInt(unit.comingSoonAt));
      }
      setComingSoonAt(formatDate(d.valueOf(), 'yyyy-MM-dd'));
    }
  }, [unit.comingSoonAt]);

  const today = formatDate(addDays(new Date(), 1).valueOf(), 'yyyy-MM-dd');

  const [getVideoUrl] = useGetVideoUrlLazyQuery();

  const handleChangeText = useCallback((key: keyof typeof validationErrors) => (value: TextInputValue) => {
    setErrors({
      ...errors,
      [key]: '',
    });
    if (key === 'uniqueName') {
      value = value.toString().replace(/\s+/g, '-').replace(/[^A-Za-z-0-9-]/g, '').toLowerCase();
    }
    onChange({[key]: value});
  }, [errors, onChange, setErrors]);

  const handleChangeLessonCount = useCallback((value: TextInputValue) => {
    onChange({suggestedLessonCount: Number(value)});
  }, [onChange]);

  const handleChangeDuration = useCallback((value: TextInputValue) => {
    onChange({suggestedDuration: value.toString()});
  }, [onChange]);

  const clearFile = useCallback(() => {
    setDataFile(undefined);
    setFileUploadingPercent(0);
  }, [setFileUploadingPercent])

  const onProgress = useCallback((percent: number) => {
    setFileUploadingPercent(percent)
  }, [setFileUploadingPercent]);

  const updateVideoContent = useCallback((popupCover: string) => {
    onChange({ popupCover });
  }, [onChange]);

  const handleCoverVideoChange = useCallback((video: Video | null) => {
    if (video) {
      onChange({ popupCover: video.data })
    }
  }, [onChange]);

  const handleSetVideoFile = useCallback(async (file: File | undefined) => {
    if (file) {
      setDataFile(file);
      try {
        const videoUrlUploadData = await getUrl({variables: {input: {metadata: file.name}}});
        if (videoUrlUploadData.data?.videoUpload?.url && videoUrlUploadData.data?.videoUpload?.uploadId) {
          setLoading(true);
          await handleUploadVideo(file, videoUrlUploadData.data?.videoUpload?.url, onProgress);
          const videoFullLink = await getVideoUrl({ variables: { uploadId: videoUrlUploadData.data?.videoUpload?.uploadId }});
          if (videoFullLink.data?.getVideoUrl?.url) {
            updateVideoContent(videoFullLink.data?.getVideoUrl?.url);
          }
          setLoading(false);
        }
      } catch {
        clearFile();
      }
    } else {
      clearFile();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearFile, getUrl, onProgress, setLoading]);

  const handleAddParentsOption = useCallback(() => {
    const curr = [...parentPreps];
    curr.push('');
    onChange({ parentBox: JSON.stringify(curr) });
  }, [onChange, parentPreps]);

  const handleParentsRemoveOption = useCallback((id: number) => {
    const curr = [...parentPreps];
    onChange({ parentBox: JSON.stringify(curr.filter((child, index) => {
      return index !== id;
    })) });
  }, [onChange, parentPreps]);

  const handleParentsChangeOption = useCallback((index: number, value: TextInputValue) => {
    const answers = [...parentPreps];
    answers[index] = value.toString()
    onChange({ parentBox: JSON.stringify(answers) });
  }, [onChange, parentPreps]);

  const handleAddTeachersOption = useCallback(() => {
    const curr = [...teacherPreps];
    curr.push('');
    onChange({ teacherBox: JSON.stringify(curr) });
  }, [onChange, teacherPreps]);

  const handleTeachersRemoveOption = useCallback((id: number) => {
    const curr = [...teacherPreps];
    onChange({ teacherBox: JSON.stringify(curr.filter((child, index) => {
      return index !== id;
    })) });
  }, [onChange, teacherPreps]);

  const handleTeachersChangeOption = useCallback((index: number, value: TextInputValue) => {
    const answers = [...teacherPreps];
    answers[index] = value.toString()
    onChange({ teacherBox: JSON.stringify(answers) });
  }, [onChange, teacherPreps]);

  const handleAddTeachsOption = useCallback(() => {
    const curr = [...answers1];
    curr.push('');
    onChange({ box1: JSON.stringify(curr) });
  }, [answers1, onChange]);

  const handleChangeSkillHeading = useCallback((value: TextInputValue) => {
    onChange({ skillsHeading: value as string });
  }, [onChange])

  const handleChangeDescription = useCallback((value: TextInputValue) => {
    onChange({ parentImage: value as string });
  }, [onChange]);

  const handleChangeTeacherDescription = useCallback((value: TextInputValue) => {
    onChange({ teacherImage: value as string });
  }, [onChange]);

  const handleAddStudensOption = useCallback(() => {
    const curr = [...answers2];
    curr.push('');
    onChange({ box2: JSON.stringify(curr) });
  }, [answers2, onChange]);

  const handleChangeHelpfulHeading = useCallback((value: TextInputValue) => {
    onChange({ helpfulHeading: value as string });
  }, [onChange]);

  const handleTeachsRemoveOption = useCallback((id: number) => {
    const curr = [...answers1];
    onChange({ box1: JSON.stringify(curr.filter((child, index) => {
      return index !== id
    })) });
  }, [answers1, onChange]);

  const handleStudentsRemoveOption = useCallback((id: number) => {
    const curr = [...answers2];
    onChange({ box2: JSON.stringify(
      curr.filter((child, index) => {
        return index !== id
      })
    ) });
  }, [answers2, onChange]);

  const handleTeachsChangeOption = useCallback((index: number, value: TextInputValue) => {
    const answers = [...answers1];
    answers[index] = value.toString();
    onChange({ box1: JSON.stringify(answers) });
  }, [answers1, onChange]);

  const handleStudentsChangeOption = useCallback((index: number, value: TextInputValue) => {
    const answers = [...answers2];
    answers[index] = value.toString()
    onChange({ box2: JSON.stringify(answers) });
  }, [answers2, onChange]);

  const handleChangeSkill = useCallback((v) => {
    onChange({ skill: v.value });
  }, [onChange]);

  // if (!unit.skill) {
  //   onChange({ skill: skillOptions[0]?.value });
  // }

  const handleChangeCountry = useCallback((v: string) => {
    onChange({ country: v });
  }, [onChange]);

  const handleChangeUnit = useCallback((v) => {
    onChange({ type: v.value });
  }, [onChange]);

  const handleChangeAge = useCallback((v) => {
    onChange({ ageGroup: v.value });
  }, [onChange]);

  const handleChangeImage = useCallback((image: string, file: File | undefined) => {
    onChange({ coverImage: file });
  }, [onChange]);

  const handleChangeDefaultUnit = useCallback((v: boolean) => {
    onChange({ default: v });
  }, [onChange]);

  const handleChangeOptions = useCallback((value: any) => {
    onChange({ courseId: value?.length ? JSON.stringify(value) : '' });
  }, [onChange]);

  const onChangeTags = useCallback((value: string[]) => {
    onChange({ tags: JSON.stringify(value) });
  }, [onChange]);

  const onChangeTopics = useCallback((value: string[]) => {
    onChange({ topics: JSON.stringify(value) });
  }, [onChange]);

  const handleChangeSensitiveUnit = useCallback((v: boolean) => {
    onChange({ sensitive: v });
  }, [onChange]);

  const handleChangeSensitiveTooltip = useCallback((value: TextInputValue) => {
    onChange({ sensitiveTooltip: value.toString() });
  }, [onChange]);

  const handleChangeIsNew = useCallback((v: boolean) => {
    onChange({ isNew: v });
  }, [onChange]);

  const handleChangeIsComingSoon = useCallback((v: boolean) => {
    onChange({ isComingSoon: v });
  }, [onChange]);

  const handleChangeIsUpdated = useCallback((v: boolean) => {
    onChange({ isUpdated: v });
  }, [onChange]);

  const handleChangeComingSoonAt = useCallback((v) => {
    onChange({'comingSoonAt': v});
  }, [onChange]);

  const handleChangeComingSoonLabelPosition = useCallback((v: string) => {
    onChange({ comingSoonLabelPosition: v });
  }, [onChange]);

  const handleChangeUpdatedLabelPosition = useCallback((v: string) => {
    onChange({ updatedLabelPosition: v });
  }, [onChange]);

  const handleChangeNewLabelPosition = useCallback((v: string) => {
    onChange({ newLabelPosition: v });
  }, [onChange]);

  const handleChangeLabel = useCallback((v: string) => {
    setLabel(v);
    handleChangeIsNew(v === labelOptions[1].value)
    handleChangeIsComingSoon(v === labelOptions[2].value)
    handleChangeIsUpdated(v === labelOptions[3].value)
  }, [setLabel, handleChangeIsNew, handleChangeIsComingSoon, handleChangeIsUpdated]);

  const handleChangeCompetency = useCallback((v) => {
    onChange({ competency: v?.length ? JSON.stringify(v) : '' });
  }, [onChange]);

  const handleChangeStudyReviewDescription = useCallback((value: TextInputValue) => {
    onChange({ unitStudyHeading: value as string });
  }, [onChange]);

  const handleStudyReviewOptionRemove = useCallback((id: number) => {
    const curr = [...studyGuides];
    onChange({ unitStudyBox: JSON.stringify(curr.filter((child, index) => {
      return index !== id;
    })) });
  }, [onChange, studyGuides]);

  const handleStudyReviewOptionsChange = useCallback((index: number, value: TextInputValue) => {
    const answers = [...studyGuides];
    answers[index] = value.toString()
    onChange({ unitStudyBox: JSON.stringify(answers) });
  }, [onChange, studyGuides]);

  const handleStudyReviewOptionAdd = useCallback(() => {
    const curr = [...studyGuides];
    curr.push('');
    onChange({ unitStudyBox: JSON.stringify(curr) });
  }, [onChange, studyGuides]);

  const unitType = useMemo(() => {
    if (unit.type === '0') {
      return unitOptions[0].value
    } else if (unit.type === '1') {
      return unitOptions[1].value
    }
    return unit.type || unitOptions[0].value
  }, [unit.type])

  if (!unit.type || unit.type === '0') {
    onChange({ type: unitOptions[0].value });
  } else if (unit.type === '1') {
    onChange({ type: unitOptions[1].value });
  }

  const handleChangeCover = useCallback((v: string) => {
    onChange({isNewCover: v === coverOptions[1].value})
  }, [onChange]);

  const handleChangeCategories = useCallback((key: any) => (option: any) => {
    let updateCategories = categories;
    if (updateCategories[key]) {
      updateCategories[key] = option.value.toString()
    } else {
      updateCategories = { ...categories, [key]: option.value.toString()}
    }
    onChange({ categories: JSON.stringify(updateCategories) });
  }, [categories, onChange]);

  const handleChangeNewLayout = useCallback((v: boolean) => {
    onChange({ isEducatorCaregiverLessonLayout: v });
  }, [onChange]);

  return (<>
    <div className={styles.row} style={{ marginTop: '15px'}}>
      <TextInput hasError={!!errors?.name} subTitle={errors?.name} label={'Name'} id={'name'} placeholder={'Add unit name'} value={unit.name} onChange={handleChangeText('name')}/>
    </div>
    <div className={styles.row} style={{ marginTop: '15px'}}>
      <TextInput hasError={!!errors?.uniqueName} subTitle={errors?.uniqueName} label={() => <div>Pretty URL <span style={{color: 'gray'}}>(If left empty, defaults to Unit Name)</span></div>}
        id={'uniqueName'} placeholder={'Add pretty URL'} value={unit?.uniqueName || ''} onChange={handleChangeText('uniqueName')}/>
      <div>
        <span style={{color: 'gray'}}>{`Sample: ${window.location.protocol}//${window.location.host}/`}</span>
        <span>{kebabCase(unit?.uniqueName || unit.name)}</span>
        <span style={{color: 'gray'}}>/lesson-title-example</span>
      </div>
    </div>
    <div className={styles.row}>
      <TextInput hasError={!!errors?.subHeading} subTitle={errors?.subHeading} label={'Sub heading'} id={'description'} placeholder={'Add a description'} value={unit.subHeading || ''} onChange={handleChangeText('subHeading')} />
    </div>
    {isHomeEdUnit ? (
        <div className={styles.row}>
          <div className={styles.unitType}>
            <Select label={'Age Group'} options={ageOptions} value={unit.ageGroup || ageOptions[0].value} onChange={handleChangeAge} doNotSort={true}/>
          </div>
        </div>
      ) : null
    }
    {isHomeEdUnit ? (
      <div className={styles.row}>
        <div className={styles.topicTitle}>Add Unit Topics <span>(This data enables Parents to filter Units by potential learnings for their child)</span></div>
        <TagsForm tags={topics} setTags={onChangeTopics} />
      </div>
    ) : null}
    {isHomeEdUnit ? null : (
      <div className={styles.row}>
        <Select label={'Parent Skill'} options={skillOptions} value={unit.skill || skillOptions[0]?.value} onChange={handleChangeSkill}/>
        {
          !!errors?.skill && (
            <div className={styles.error}>{errors?.skill}</div>
          )
        }
      </div>
    )}
    <div className={styles.newBox}>
      <div className={`${styles.subRadioTitle} ${styles.row}`}>Labels</div>
        <RadioGroup
          value={label}
          options={labelOptions}
          onChange={handleChangeLabel}
          name={''}
          hasError={false}
          subTitle={""}
        />
        {
          unit.isComingSoon &&
          <>
            <div className={`${styles.comingSoonAtTitle}`}>Coming Soon At:</div>
            <TextInput
              className={styles.comingSoonAt}
              label={''}
              id={'comingSoonAt'}
              value={comingSoonAt}
              onChange={handleChangeComingSoonAt}
              type={'date'}
              min={today}
            />
            <div className={styles.comingSoonAt}>
              <div className={styles.topicTitle}>Coming Soon label Position</div>
              <RadioGroup
                value={unit.comingSoonLabelPosition || gradeLabelPositionOptions[3].value}
                options={gradeLabelPositionOptions}
                onChange={handleChangeComingSoonLabelPosition}
                name={''}
                hasError={false}
                subTitle={""}
              />
            </div>
          </>
        }
        {
          unit.isNew && 
          <div className={styles.comingSoonAt}>
            <div className={styles.topicTitle}>New label Position</div>
            <RadioGroup
              value={unit.newLabelPosition || gradeLabelPositionOptions[3].value}
              options={gradeLabelPositionOptions}
              onChange={handleChangeNewLabelPosition}
              name={''}
              hasError={false}
              subTitle={""}
            />
          </div>
        }
        {
          unit.isUpdated && 
          <div className={styles.comingSoonAt}>
            <div className={styles.topicTitle}>Updated label Position</div>
            <RadioGroup
              value={unit.updatedLabelPosition || gradeLabelPositionOptions[4].value}
              options={gradeLabelPositionOptions}
              onChange={handleChangeUpdatedLabelPosition}
              name={''}
              hasError={false}
              subTitle={""}
            />
          </div>
        }
    </div>
    <div className={styles.newBox}>
      <div className={`${styles.subRadioTitle} ${styles.row}`}>Unit Cover Type</div>
        <RadioGroup
          value={cover}
          options={coverOptions}
          onChange={handleChangeCover}
          name={''}
          hasError={false}
          subTitle={""}
        />
    </div>
    <div className={styles.row}>
      <div className={styles.uploadertitle}>Add a unit cover <span>(The image that will be visible on the Units Dashboard)</span></div>
      <ImageUploadForm url={unit.coverImage || undefined} onChange={handleChangeImage}/>
    </div>
    <div className={styles.row}>
      <div className={styles.videoUploadTitle}>Add a unit cover video</div>
      <VideoForm
        data={unit?.popupCover || undefined}
        dataFile={dataFile}
        onChange={handleSetVideoFile}
        onChangeVideo={handleCoverVideoChange}
        isUploading={fileUploadingPercent > 0 && fileUploadingPercent < 100}
        uploadingPercent={fileUploadingPercent}
        isCoverVideo
      />
    </div>
    {isHomeEdUnit ? null : (
      <>
      <div className={styles.row}>
        <RadioGroup
          value={unit.country || 'All Countries'}
          options={options}
          onChange={handleChangeCountry}
          name={''}
          hasError={false}
          subTitle={""}
        />
      </div>
      <div className={styles.row} style={{marginTop: 20}}>
        <div className={styles.subRadioTitle}>Educator/Caregiver Lesson Layout: </div>
        <Checkbox id={'Educator-Caregiver-Lesson-Layout'} checked={!!unit.isEducatorCaregiverLessonLayout} onChange={handleChangeNewLayout} label={'Educator/Caregiver Lesson Layout'}/>
      </div>
      <div className={styles.subTitle}>
        <Checkbox id={'defaultUnit'} checked={!!unit.default} onChange={handleChangeDefaultUnit} label={isHomeEdUnit ? 'This Unit is a Default Unit' : 'This Unit is a Default Unit for:'}/>
        {unit.default ? <>
            <SelectMulti className={styles.unitCourse}
              inline={false}
              label={''}
              options={coursesOptions}
              onChange={handleChangeOptions}
              value={courses}/>
              <div className={styles.unitType}>
                <Select label={'Unit Type'} options={unitOptions} value={unitType} onChange={handleChangeUnit}/>
              </div>
          </> : null
        }
      </div>
    </>
    )}
    <div className={styles.subTitle}>Overview</div>
    <div className={styles.row}>
      <TextInput label={'Suggested pace'} id={'pace'} placeholder={'Add suggested pace'} value={unit.pace || ''} onChange={handleChangeText('pace')}/>
    </div>
    <div className={styles.row}>
      <div className={styles.videoUploadTitle}>Add Unit Tags</div>
      <TagsForm tags={tags} setTags={onChangeTags} />
    </div>
    <div className={styles.row}>
      <div className={styles.videoUploadTitle}>Competency</div>
      <SelectMulti className={styles.unitCourse}
              inline={false}
              label={''}
              options={standaloneLessonsSelCompetencies}
              onChange={handleChangeCompetency}
              value={competency || standaloneLessonsSelCompetencies[0].value}/>
    </div>

    {isHomeEdUnit ? (
      <div className={styles.overview}>
        <div className={styles.checkView}>
          <div className={`${styles.subTitle} ${styles.row}`}>Parent Overview</div>
          <div className={styles.statements}>
            <AnswersForm
              description={unit.parentImage || 'View Unit Preparation Materials:'}
              answers={parentPreps}
              onUpdateHeading={handleChangeDescription}
              showError={!!errors?.parentPreps}
              onRemove={handleParentsRemoveOption}
              onChange={handleParentsChangeOption}
              onAdd={handleAddParentsOption}
            />
          </div>
        </div>
      </div>
    ) : (
      <div className={styles.overview}>
        <div className={styles.checkView}>
          <div className={`${styles.subTitle} ${styles.row}`}>Teacher Overview</div>
          <div className={styles.statements}>
            <AnswersForm
              description={unit.teacherImage || 'View Unit Preparation Materials:'}
              answers={teacherPreps}
              onUpdateHeading={handleChangeTeacherDescription}
              showError={!!errors?.teacherPreps}
              onRemove={handleTeachersRemoveOption}
              onChange={handleTeachersChangeOption}
              onAdd={handleAddTeachersOption}
            />
          </div>
        </div>
      </div>
    )}
    {unit.isNewCover && <>
      <div className={`${styles.overview}  ${styles.row}`}>
        <div className={styles.categoryView}>
          <div className={`${styles.subTitle} ${styles.row}`}>Categories</div>
          <div className={styles.categorySubBox}>
            <div className={styles.categoryRowBox}>
              <div className={styles.categoryBox}>
                <Select label={'Format'} showSearch={false} options={categoryStructureOptions} value={structure} onChange={handleChangeCategories('Structure')}/>
              </div>
              <div className={styles.categoryBox}>
                <Select label={'Skill'} showSearch={false} options={categorySkillsOptions} value={skill} onChange={handleChangeCategories('Skill')}/>
              </div>
              <div className={styles.categoryBox}>
                <Select label={'Comfort Level'} showSearch={false} options={categoryComfortLevelOptions} value={comfortLevel} onChange={handleChangeCategories('Comfort Level')}/>
              </div>
            </div>
            <div className={styles.categoryRowBox}>
              <div className={styles.categoryBox}>
                <Select label={'Mood'} showSearch={false} options={categoryMoodOptions1} value={mood} onChange={handleChangeCategories('Mood')}/>
              </div>
              <div className={styles.categoryBox}>
                <Select label={'Exercise Style'} showSearch={false} options={categoryExerciseStyleOptions} value={exerciseStyle} onChange={handleChangeCategories('Exercise Style')}/>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div style={{width: "20%"}}>
        <div className={styles.row}>
          <TextInput label={'How many lessons?'} id={'lesson_count'} type='number' value={unit.suggestedLessonCount === 0 ? '' : unit.suggestedLessonCount ?? ''} onChange={handleChangeLessonCount}/>
        </div>
        <div className={styles.row}>
          <TextInput label={'Suggested Duration (mins)'} id={'durations'} value={unit.suggestedDuration ?? ''} onChange={handleChangeDuration}/>
        </div>
      </div>
    </>}
    <hr className={styles.horizDivider} />
    <div className={styles.overview}>
      <div className={styles.checkView}>
        <div className={`${styles.subTitle} ${styles.row}`}>Unit Study Review</div>
        <div className={styles.statements}>
          <AnswersForm
            description={unit.unitStudyHeading || 'This Study Guide helps Teachers:'}
            answers={studyGuides}
            onUpdateHeading={handleChangeStudyReviewDescription}
            showError={!!errors?.studyGuides}
            onRemove={handleStudyReviewOptionRemove}
            onChange={handleStudyReviewOptionsChange}
            onAdd={handleStudyReviewOptionAdd}
          />
        </div>
      </div>
    </div>
    <div className={styles.overview}>
      <div className={styles.checkView}>
        <div className={`${styles.subTitle} ${styles.row}`}>Unit Overview 1 Heading</div>
        <div className={styles.statements}>
          <AnswersForm
            description={unit.skillsHeading || 'This Unit teaches skills for:'}
            answers={answers1}
            showError={!!errors?.answers1}
            onUpdateHeading={handleChangeSkillHeading}
            onRemove={handleTeachsRemoveOption}
            onChange={handleTeachsChangeOption}
            onAdd={handleAddTeachsOption}
          />
        </div>
        <div className={`${styles.subTitle} ${styles.row}`}>Unit Overview 2 Heading</div>
        <div className={styles.statements}>
          <AnswersForm
            description={unit.helpfulHeading || 'This unit is helpful for students who:'}
            answers={answers2}
            showError={!!errors?.answers2}
            onUpdateHeading={handleChangeHelpfulHeading}
            onRemove={handleStudentsRemoveOption}
            onChange={handleStudentsChangeOption}
            onAdd={handleAddStudensOption}
          />
        </div>
      </div>
      <div className={styles.preview}>
        <PreviewForm title1={unit.skillsHeading || 'This Unit teaches skills for:'} answers1={answers1} title2={unit.helpfulHeading || 'This unit is helpful for students who:'} answers2={answers2}/>
      </div>
    </div>
    <div className={styles.subTitle}>
      <Checkbox id={'sensitiveUnit'} checked={!!unit.sensitive} onChange={handleChangeSensitiveUnit} label={'This Unit contains Sensitive Content'}/>
      {unit.sensitive &&
        <MultiLineTextInput allowSpecialCharacters={false} id='sensitiveUnit' hasError={false} subTitle={''} label={''} value={unit.sensitiveTooltip ?? 'This Unit contains sensitive content. We recommend previewing the lessons to ensure you are comfortable with material.'} onChange={handleChangeSensitiveTooltip} />}
    </div>
  </>);
}

export default UnitEditTabContent;
